import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import React, { useContext, useEffect } from "react";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { CMSContext } from "../components/CaseManagement/context/CaseManagmentContext";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";

const iconButtonStyle = {
  color: "#ffffff",
  background: "rgba(255, 255, 255, 0.15)",
  mr: 1.5,
  "&:hover": {
    background: "rgba(255, 255, 255, 0.25)",
  },
  p: 0.5,
  borderRadius: "6px",
};

function ReferenceCaseDialog({
  open,
  onClose,
  selectedReferenceCase,
  setSelectedReferenceCase,
  caseNoteError,
  isAddCaseLoading,
  handleAddCaseToCaseManagement,
  type = "case",
}) {
  const { cases, loading, fetchCases } = useContext(CMSContext);

  useEffect(() => {
    if (open) {
      fetchCases();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: { xs: "12px", sm: "24px" },
          boxShadow: "0 8px 32px 0 rgba(60, 80, 177, 0.13)",
          background: "linear-gradient(135deg, #fff 80%, #f8fafc 100%)",
          margin: { xs: 1, lg: 0 },
        },
      }}
      BackdropProps={{
        sx: {
          background: "rgba(30,41,59,0.25)",
          backdropFilter: "blur(2px)",
        },
      }}
      sx={{
        "& .MuiModal-root": {
          outline: "none",
        },
        "& .MuiPaper-root": {
          borderRadius: "24px",
          boxShadow: 24,
          p: 0,
          width: 400,
        },
      }}
    >
      <Box
        sx={{
          minHeight: 48,
          display: "flex",
          alignItems: "center",
          px: { xs: 1.5, sm: 2 },
          background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <IconButton onClick={onClose} sx={iconButtonStyle} aria-label="Back">
          <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#ffffff",
              fontSize: "0.875rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {`Add this ${type} as reference ${type}`}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ overflow: "visible", padding: "16px 24px" }}>
        <TextField
          sx={{
            mt: 1,
            mb: { xs: 0, lg: 1 },
            bgcolor: "white",
            "& .MuiInputBase-root": { fontSize: "13px" },
          }}
          label="Cases"
          select
          fullWidth
          size="small"
          value={selectedReferenceCase}
          onChange={(e) => {
            const selectedValue = e.target.value;
            setSelectedReferenceCase(selectedValue);
          }}
          InputLabelProps={{ sx: { fontSize: "13px" } }}
        >
          {loading && (
            <MenuItem
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "13px",
                display: "flex",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <CircularProgress size={16} sx={{ color: "#696969" }} />
              <span>Loading...</span>
            </MenuItem>
          )}
          {Array.isArray(cases) &&
            cases?.map((group) => (
              <MenuItem
                sx={{
                  maxWidth: "400px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "13px",
                }}
                key={group._id}
                value={group._id}
              >
                {group.caseTitle}
              </MenuItem>
            ))}
        </TextField>
      </DialogContent>
      <DialogActions
        sx={{ mt: 1, justifyContent: "flex-end", padding: "8px 24px 16px 24px" }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          color="error"
          sx={{
            fontSize: { xs: "13px", lg: "14px" },
            borderRadius: "24px",
            padding: { xs: "6px 12px", lg: "8px 24px" },
            textTransform: "none",
            fontWeight: "bold",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            isAddCaseLoading && (
              <CircularProgress size={16} sx={{ color: "white" }} />
            )
          }
          sx={{
            fontSize: { xs: "13px", lg: "14px" },
            borderRadius: "24px",
            padding: { xs: "6px 12px", lg: "8px 24px" },
            textTransform: "none",
            fontWeight: "bold",
            backgroundColor: "#003D78",
            "&:hover": { backgroundColor: "primary.main", color: "#fff" },
            transition: "background-color 0.3s ease-in-out",
          }}
          onClick={handleAddCaseToCaseManagement}
          disabled={!selectedReferenceCase || caseNoteError}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReferenceCaseDialog;
